/**
 * Общая логика для генерации подключений коллекторов
 * Используется в FloorCollectorEditor и DistributionCollectorEditor
 */

import fittingsData from '../fittingsData';
import {
  extractDnFromFlangeKey,
  extractSizeFromNakGaikaKey,
  extractSizeFromShaibaNarRezba,
  getPipeDiameterForDn,
  getPipeDiameterForNakGaika,
  getPipeDiameterForShaibaNarRezba,
  getShaibaKeyForFitting as getShaibaKeyFromUtils,
  nakShaibaPipeMeasure,
  endFittingAutoLength,
} from './collectorUtils';
import {
  determineThickness,
} from './pipeThicknessUtils';

// Константы
export const BORTSHAIBA_LENGTH = 10; // мм
export const INTERMEDIATE_PIPE_MIN_LENGTH = 50; // минимальная длина промежуточной трубы

// Счётчик для генерации уникальных ID
let idCounter = 0;
const generateUniqueId = () => {
  idCounter += 1;
  return `${Date.now()}-${idCounter}`;
};

/**
 * Определяет тип фланца
 * @param {string} fittingId - ID фитинга
 * @returns {'welded'|'free'|null} - тип фланца или null
 */
export const getFlangeType = (fittingId) => {
  if (!fittingId || !fittingId.startsWith('Flanec_')) return null;
  if (fittingId.includes('_welded')) return 'welded';
  return 'free'; // galv, ss, ss_stamped = свободные
};

/**
 * Проверяет нужна ли шайба/бортшайба для фитинга
 * @param {string} fittingId - ID фитинга
 * @returns {boolean}
 */
export const needsWasher = (fittingId) => {
  if (!fittingId) return false;
  const flangeType = getFlangeType(fittingId);
  return flangeType === 'free' || fittingId.startsWith('Nak_Gaika_');
};

/**
 * Рассчитывает длину промежуточной трубы для приварных фланцев
 * @param {string} fittingId - ID фитинга
 * @param {number} userLength - пользовательская длина
 * @returns {number} - длина промежуточной трубы
 */
export const getIntermediatePipeLength = (fittingId, userLength) => {
  if (!fittingId) return 0;
  const flangeType = getFlangeType(fittingId);
  if (flangeType === 'welded') {
    return Math.max(Number(userLength) || 0, INTERMEDIATE_PIPE_MIN_LENGTH);
  }
  return 0;
};

/**
 * Получает ключ бортшайбы для фитинга (реэкспорт из collectorUtils)
 */
export const getShaibaKeyForFitting = getShaibaKeyFromUtils;

/**
 * Получает диаметр трубы для фитинга
 * @param {string} fittingId - ID фитинга
 * @returns {number} - диаметр трубы
 */
export const getPipeDiameterForFitting = (fittingId) => {
  if (!fittingId) return 33.7;
  
  // Для фланцев
  if (fittingId.startsWith('Flanec_')) {
    const dn = extractDnFromFlangeKey(fittingId);
    if (dn) {
      const diameter = getPipeDiameterForDn(dn);
      if (diameter) return diameter;
    }
  }
  
  // Для накидных гаек
  if (fittingId.startsWith('Nak_Gaika_')) {
    const size = extractSizeFromNakGaikaKey(fittingId);
    if (size) {
      const diameter = getPipeDiameterForNakGaika(size);
      if (diameter) return diameter;
    }
  }
  
  // Для шайб с наружной резьбой
  if (fittingId.startsWith('Shaib_Nar_Rezba_')) {
    const size = extractSizeFromShaibaNarRezba(fittingId);
    if (size) {
      const diameter = getPipeDiameterForShaibaNarRezba(size);
      if (diameter) return diameter;
    }
  }
  
  return 33.7; // значение по умолчанию
};

/**
 * Генерирует подключение коллектора (труба или фитинг с промежуточными элементами)
 * @param {Object} params - параметры подключения
 * @returns {Object} - объект с массивами cylinders, fittings, welds, balloons и информацией о длине
 */
export const generateConnection = (params) => {
  const {
    connectionType,        // 'pipe' или 'fitting'
    fittingId,            // ID фитинга (если connectionType === 'fitting')
    pipeDiameter,         // диаметр трубы (если connectionType === 'pipe')
    pipeLength,           // длина трубы (если connectionType === 'pipe')
    fittingLength,        // вылет фитинга
    startX,               // начальная X координата
    y,                    // Y координата центра коллектора
    direction,            // 'left' или 'right'
    mainPipeId,           // ID основного коллектора
    mainPipeDiameter,     // диаметр основной трубы (для определения толщины)
    prefix,               // префикс для ID элементов (например, 'supply-left')
    balloonYOffset,       // смещение позиций по Y (положительное вниз, отрицательное вверх)
  } = params;
  
  const cylinders = [];
  const fittings = [];
  const welds = [];
  const balloons = [];
  
  let totalLength = 0;
  let mainElementId = null;
  let intermediatePipeId = null;
  let bortshaibaId = null;
  
  if (connectionType === 'pipe') {
    // Простое трубное подключение
    const pipeId = `cyl-${prefix}-conn-${generateUniqueId()}`;
    const diameter = pipeDiameter || 33.7;
    const length = pipeLength || 100;
    mainElementId = pipeId;
    totalLength = length;
    
    const xPos = direction === 'left' ? startX - length : startX;
    const pipeThickness = mainPipeDiameter ? determineThickness(mainPipeDiameter, diameter, 'Матовый') : 'x2';
    
    cylinders.push({
      id: pipeId,
      x: xPos,
      y: y - diameter / 2,
      width: length,
      height: diameter,
      pipeSpec: { shape: 'round', diameter, thickness: pipeThickness, coating: 'Матовый' },
    });
    
    welds.push({ 
      id: `weld-${prefix}-conn-${generateUniqueId()}`, 
      x: startX, 
      y, 
      objects: [mainPipeId, pipeId] 
    });
    
    balloons.push({
      id: `balloon-${prefix}-conn-${generateUniqueId()}`,
      x: xPos + length / 2,
      y: y + balloonYOffset,
      text: `Труба Ø${diameter}`,
      objects: [pipeId]
    });
    
  } else if (connectionType === 'fitting' && fittingId) {
    // Фитинговое подключение с возможной промежуточной трубой и бортшайбой
    const fittingInfo = fittingsData[fittingId];
    if (!fittingInfo) {
      return { cylinders, fittings, welds, balloons, totalLength, mainElementId, intermediatePipeId, bortshaibaId };
    }
    
    const flangeType = getFlangeType(fittingId);
    const needsIntermediatePipe = flangeType === 'welded';
    const needsBortshaiba = needsWasher(fittingId);
    
    let currentPos = startX;
    
    // 1. Промежуточная труба (для приварных фланцев)
    if (needsIntermediatePipe) {
      const intermediatePipeLength = getIntermediatePipeLength(fittingId, fittingLength);
      const diameter = getPipeDiameterForFitting(fittingId);
      intermediatePipeId = `cyl-${prefix}-intermediate-${generateUniqueId()}`;
      
      totalLength += intermediatePipeLength;
      
      const xPos = direction === 'left' ? currentPos - intermediatePipeLength : currentPos;
      const intermediateThickness = mainPipeDiameter ? determineThickness(mainPipeDiameter, diameter, 'Матовый') : 'x2';
      
      cylinders.push({
        id: intermediatePipeId,
        x: xPos,
        y: y - diameter / 2,
        width: intermediatePipeLength,
        height: diameter,
        pipeSpec: { shape: 'round', diameter, thickness: intermediateThickness, coating: 'Матовый' },
      });
      
      welds.push({ 
        id: `weld-${prefix}-intermediate-${generateUniqueId()}`, 
        x: currentPos, 
        y, 
        objects: [mainPipeId, intermediatePipeId] 
      });
      
      balloons.push({
        id: `balloon-${prefix}-intermediate-${generateUniqueId()}`,
        x: xPos + intermediatePipeLength / 2,
        y: y + balloonYOffset,
        text: `Труба Ø${diameter}`,
        objects: [intermediatePipeId]
      });
      
      currentPos = direction === 'left' ? currentPos - intermediatePipeLength : currentPos + intermediatePipeLength;
    }
    
    // 2. Бортшайба (для свободных фланцев и накидных гаек)
    if (needsBortshaiba) {
      const shaibaKey = getShaibaKeyForFitting(fittingId);
      if (shaibaKey && fittingsData[shaibaKey]) {
        const shaibaInfo = fittingsData[shaibaKey];
        bortshaibaId = `fit-${prefix}-bortshaiba-${generateUniqueId()}`;
        totalLength += BORTSHAIBA_LENGTH;
        
        const rotation = direction === 'left' ? -90 : 90;
        
        fittings.push({
          id: bortshaibaId,
          key: shaibaKey,
          itemKey: shaibaKey,
          x: 0, y: 0,
          width: shaibaInfo.diameter,
          height: BORTSHAIBA_LENGTH,
          groupX: currentPos,
          groupY: y,
          rotation,
          offsetX: shaibaInfo.diameter / 2,
          offsetY: BORTSHAIBA_LENGTH,
        });
        
        const xPos = direction === 'left' ? currentPos - BORTSHAIBA_LENGTH / 2 : currentPos + BORTSHAIBA_LENGTH / 2;
        
        balloons.push({
          id: `balloon-${prefix}-bortshaiba-${generateUniqueId()}`,
          x: xPos,
          y: y + balloonYOffset,
          text: shaibaInfo.name || 'Бортшайба',
          objects: [bortshaibaId]
        });
        
        currentPos = direction === 'left' ? currentPos - BORTSHAIBA_LENGTH : currentPos + BORTSHAIBA_LENGTH;
      }
    }
    
    // 3. Основной фитинг
    const fittingElementId = `fit-${prefix}-conn-${generateUniqueId()}`;
    const length = endFittingAutoLength(fittingId, fittingLength, fittingInfo.length);
    
    mainElementId = fittingElementId;
    totalLength += length;
    
    const rotation = direction === 'left' ? -90 : 90;
    
    fittings.push({
      id: fittingElementId,
      key: fittingId,
      itemKey: fittingId,
      x: 0, y: 0,
      width: fittingInfo.diameter,
      height: length,
      groupX: currentPos,
      groupY: y,
      rotation,
      offsetX: fittingInfo.diameter / 2,
      offsetY: length,
    });
    
    if (!needsIntermediatePipe) {
      welds.push({ 
        id: `weld-${prefix}-conn-${generateUniqueId()}`, 
        x: startX, 
        y, 
        objects: [mainPipeId, fittingElementId] 
      });
    }
    
    const xPos = direction === 'left' ? currentPos - length / 2 : currentPos + length / 2;
    
    balloons.push({
      id: `balloon-${prefix}-conn-${generateUniqueId()}`,
      x: xPos,
      y: y + balloonYOffset,
      text: fittingInfo.name || fittingId,
      objects: [fittingElementId]
    });
  }
  
  return { 
    cylinders, 
    fittings, 
    welds, 
    balloons, 
    totalLength, 
    mainElementId, 
    intermediatePipeId, 
    bortshaibaId 
  };
};

/**
 * Генерирует размерные линии для промежуточной трубы
 * @param {Object} params - параметры
 * @returns {Object|null} - объект размерной линии или null
 */
export const generateIntermediatePipeDimensionLine = (params) => {
  const {
    intermediatePipeId,
    fittingId,
    fittingLength,
    startX,
    y,
    pipeDiameter,
    direction,
    yOffset,
  } = params;
  
  if (!intermediatePipeId) return null;
  
  const length = getIntermediatePipeLength(fittingId, fittingLength);
  
  const x1 = direction === 'left' ? startX - length : startX;
  const x2 = direction === 'left' ? startX : startX + length;
  
  return {
    isHorizontal: true,
    lockOrientation: true,
    angle: 0,
    x1,
    y1: y - pipeDiameter / 2,
    x2,
    y2: y - pipeDiameter / 2,
    position: { x: 0, y: y + yOffset },
    value: Math.round(length).toString(),
    rawValue: length,
    extensionLines: [],
    precision: 0,
    textPosition: { x: 0, y: 0 },
    textFontFamily: 'Arial',
    textFontStyle: 'italic',
    textColor: '#ff0000',
    elements: [
      { objectId: intermediatePipeId, objectType: 'cylinder', x: x1, y: y - pipeDiameter / 2, type: 'left' },
      { objectId: intermediatePipeId, objectType: 'cylinder', x: x2, y: y - pipeDiameter / 2, type: 'right' },
    ],
  };
};

/**
 * Генерирует подключение с учетом заглушек/переходов (для распределительного коллектора)
 * @param {Object} params - параметры подключения
 * @returns {Object} - объект с массивами cylinders, fittings, welds, balloons и информацией о длине
 */
export const generateConnectionWithCaps = (params) => {
  const {
    connectionType,        // 'pipe' или 'fitting'
    fittingId,            // ID фитинга
    pipeDiameter,         // диаметр трубы (если connectionType === 'pipe')
    pipeLength,           // длина трубы (если connectionType === 'pipe')
    fittingLength,        // вылет фитинга (общая длина: переход + труба + бортшайба)
    startX,               // начальная X координата (край гребёнки или заглушки)
    y,                    // Y координата центра коллектора
    direction,            // 'left' или 'right'
    mainPipeId,           // ID основного коллектора
    prefix,               // префикс для ID элементов
    balloonYOffset,       // смещение позиций по Y
    // Параметры заглушек/переходов
    hasCap,               // есть ли заглушка/переход
    capElementType,       // 'cap' или 'reducer'
    capType,              // 'straight' или 'elliptical' (для заглушек)
    capLength,            // длина заглушки/перехода
    mainPipeDiameter,     // диаметр основной трубы (гребёнки)
    separatorDiameter,    // диаметр гидроразделителя (если подключение к нему — для insertionDepth)
    shape = 'round',      // форма профиля ('round' или 'square')
    // eslint-disable-next-line no-unused-vars
    profile,              // профиль (для квадратных, например '80x80') - передается для будущего использования
  } = params;
  
  
  const cylinders = [];
  const fittings = [];
  const welds = [];
  const balloons = [];
  
  let totalLength = 0;
  let mainElementId = null;
  let intermediatePipeId = null;
  let bortshaibaId = null;
  
  if (connectionType === 'pipe') {
    // Простое трубное подключение (без изменений)
    const pipeId = `cyl-${prefix}-pipe-${generateUniqueId()}`;
    mainElementId = pipeId;
    totalLength = Number(pipeLength) || 100;
    
    const xPos = direction === 'left' ? startX - totalLength : startX;
    const pipeThickness = mainPipeDiameter ? determineThickness(mainPipeDiameter, pipeDiameter, 'Матовый') : 'x2';
    
    cylinders.push({
      id: pipeId,
      x: xPos,
      y: y - pipeDiameter / 2,
      width: totalLength,
      height: pipeDiameter,
      pipeSpec: { shape: 'round', diameter: pipeDiameter, thickness: pipeThickness, coating: 'Матовый' },
    });
    
    welds.push({ 
      id: `weld-${prefix}-pipe-${generateUniqueId()}`, 
      x: startX, 
      y, 
      objects: [mainPipeId, pipeId] 
    });
    
    balloons.push({
      id: `balloon-${prefix}-pipe-${generateUniqueId()}`,
      x: xPos + totalLength / 2,
      y: y + balloonYOffset,
      text: `Труба Ø${pipeDiameter}`,
      objects: [pipeId]
    });
    
  } else if (connectionType === 'fitting' && fittingId) {
    // Фитинговое подключение с учетом заглушек/переходов
    const fittingInfo = fittingsData[fittingId];
    if (!fittingInfo) {
      return { cylinders, fittings, welds, balloons, totalLength, mainElementId, intermediatePipeId, bortshaibaId };
    }
    
    const isFlange = fittingId.startsWith('Flanec_');
    const isFreeFlange = isFlange && getFlangeType(fittingId) === 'free';
    const isWeldedFlange = isFlange && getFlangeType(fittingId) === 'welded';
    const isNakGaika = fittingId.startsWith('Nak_Gaika_');
    const isShaibaNarRezba = fittingId.startsWith('Shaib_Nar_Rezba_');
    const needsBortshaiba = needsWasher(fittingId);
    
    // Применяем дефолтные значения для квадратных профилей
    let effectiveFittingLength = fittingLength;
    if (!fittingLength && shape === 'square') {
      if (isNakGaika || isShaibaNarRezba) {
        effectiveFittingLength = 58;
      }
    }
    
    // Получаем диаметр трубы под фитинг
    let pipeDiameterForFitting;
    if (isFlange) {
      const dn = extractDnFromFlangeKey(fittingId);
      pipeDiameterForFitting = getPipeDiameterForDn(dn);
    } else if (isNakGaika) {
      const size = extractSizeFromNakGaikaKey(fittingId);
      pipeDiameterForFitting = getPipeDiameterForNakGaika(size);
    } else if (isShaibaNarRezba) {
      const size = extractSizeFromShaibaNarRezba(fittingId);
      pipeDiameterForFitting = getPipeDiameterForShaibaNarRezba(size);
    }
    
    // Длина бортшайбы/шайбы
    let bortshaibaLength = 0;
    const shaibaKey = getShaibaKeyFromUtils(fittingId);
    if (needsBortshaiba && shaibaKey && fittingsData[shaibaKey]) {
      bortshaibaLength = fittingsData[shaibaKey].length || BORTSHAIBA_LENGTH;
    }
    // Для шайб с наружной резьбой: вычитаем длину самой шайбы из вылета (труба + шайба = вылет)
    // (как в этажном коллекторе)
    if (isShaibaNarRezba) {
      bortshaibaLength = fittingInfo.length || BORTSHAIBA_LENGTH;
    }
    
    const hasReducerFromCaps = hasCap && capElementType === 'reducer';
    const hasEllipticalCap = hasCap && capElementType === 'cap' && capType === 'elliptical';
    const hasStraightCap = hasCap && capElementType === 'cap' && capType === 'straight';
    const needsReducer = pipeDiameterForFitting && pipeDiameterForFitting !== mainPipeDiameter;
    
    // Проверяем нужна ли сложная логика (с промежуточной трубой)
    const shouldUseComplexLogic = isFreeFlange || 
                                   (isWeldedFlange && (needsReducer || hasReducerFromCaps || hasEllipticalCap || hasStraightCap)) || 
                                   isNakGaika || 
                                   isShaibaNarRezba;
    
    let currentPos = startX;
    
    if (shouldUseComplexLogic) {
      // === СЛОЖНАЯ ЛОГИКА: с промежуточной трубой ===
      
      // ЛОГИКА 1: Диаметры совпадают (без перехода/заглушки)
      if (!needsReducer && !hasReducerFromCaps && !hasEllipticalCap && !hasStraightCap) {
        // Для накидных гаек и свободных фланцев: бортшайба впритык к гребёнке
        if ((isNakGaika || isFreeFlange) && shaibaKey && fittingsData[shaibaKey]) {
          const shaibaInfo = fittingsData[shaibaKey];
          bortshaibaId = `fit-${prefix}-bortshaiba-${generateUniqueId()}`;
          totalLength += bortshaibaLength;
          
          const rotation = direction === 'left' ? -90 : 90;
          
          fittings.push({
            id: bortshaibaId,
            key: shaibaKey,
            itemKey: shaibaKey,
            x: 0, y: 0,
            width: shaibaInfo.diameter,
            height: bortshaibaLength,
            ...(shaibaInfo.snapPoints && {
              snapPoints: shaibaInfo.snapPoints,
              useOriginalSize: true,
            }),
            correctionOffset: 3,
            groupX: currentPos,
            groupY: y,
            rotation,
            offsetX: shaibaInfo.diameter / 2,
            offsetY: bortshaibaLength,
          });
          
          const xPos = direction === 'left' ? currentPos - bortshaibaLength / 2 : currentPos + bortshaibaLength / 2;
          
          balloons.push({
            id: `balloon-${prefix}-bortshaiba-${generateUniqueId()}`,
            x: xPos,
            y: y + balloonYOffset,
            text: shaibaInfo.name || 'Бортшайба',
            objects: [bortshaibaId]
          });
          
          // currentPos НЕ двигаем, так как фланец/гайка "надевается" поверх трубы
        }
        // Для шайб с наружной резьбой: сама шайба впритык к гребёнке
        else if (isShaibaNarRezba) {
          // Для шайбы с нар. резьбой диаметры совпадают → она входит в общий размер
          // currentPos остаётся на краю гребенки, сама шайба будет создана ниже
        }
        
        // Теперь создаём сам фланец/гайку/шайбу (после борт-шайбы или на краю гребенки)
        const fittingElementId = `fit-${prefix}-conn-${generateUniqueId()}`;
        const fittingActualLength = fittingInfo.length;
        
        mainElementId = fittingElementId;
        const rotation = direction === 'left' ? -90 : 90;
        
        // Для шайбы с наружной резьбой: добавляем к totalLength (она входит в размер)
        // Для накидной гайки и свободного фланца: НЕ добавляем (они "надеты" на трубу)
        if (isShaibaNarRezba) {
          totalLength += fittingActualLength;
        }
        
        // Позиция фланца/гайки/шайбы
        let fittingGroupX;
        if (isFreeFlange || isNakGaika) {
          // Для свободных фланцев и накидных гаек: от бортшайбы + длина
          // (как в этажном: flangeX = leftBortshaibaTop + fLength)
          fittingGroupX = direction === 'left' 
            ? currentPos + fittingActualLength 
            : currentPos - fittingActualLength;
        } else if (isShaibaNarRezba) {
          // Для шайбы с наружной резьбой: впритык к гребенке
          // (как в этажном: flangeX = connectionPointX, где connectionPointX = mainPipeStartX)
          fittingGroupX = currentPos;
        } else {
          // Для приварных фланцев без переходов: от гребенки + длина
          fittingGroupX = direction === 'left' 
            ? currentPos + fittingActualLength 
            : currentPos - fittingActualLength;
        }
        
        // Логика для "Измененное значение"
        let dimensionOverride = {};
        const shouldApplyOverride = fittingLength === null || fittingLength === undefined || 
                                    fittingLength === 40 || fittingLength === '40' || 
                                    Number(fittingLength) === 40;
        
        if (shouldApplyOverride) {
          if (isNakGaika) {
            dimensionOverride = {
              overrideEnabled: false,
              overrideText: '32',
              bortshaibaId: bortshaibaId,
            };
          } else if (isShaibaNarRezba) {
            dimensionOverride = {
              overrideEnabled: false,
              overrideText: '32',
            };
          }
        }
        
        fittings.push({
          id: fittingElementId,
          key: fittingId,
          itemKey: fittingId,
          x: 0, y: 0,
          width: fittingInfo.diameter,
          height: fittingActualLength,
          groupX: fittingGroupX,
          groupY: y,
          rotation,
          offsetX: fittingInfo.diameter / 2,
          offsetY: fittingActualLength,
          ...dimensionOverride,
          // IGES: торцевые подключения НЕ создают отверстия
          igesCreateHole: false,
        });
        
        const xPos = direction === 'left' ? fittingGroupX - fittingActualLength / 2 : fittingGroupX + fittingActualLength / 2;
        
        balloons.push({
          id: `balloon-${prefix}-conn-${generateUniqueId()}`,
          x: xPos,
          y: y + balloonYOffset,
          text: fittingInfo.name || fittingId,
          objects: [fittingElementId]
        });
        
        // Для шайбы с наружной резьбой, когда диаметры совпадают: используем её ID для размерных линий
        if (isShaibaNarRezba) {
          bortshaibaId = fittingElementId;
        }
      }
      // ЛОГИКА 1.5: Диаметры НЕ совпадают, но заглушка не установлена (нужна промежуточная труба)
      else if (needsReducer && !hasReducerFromCaps && !hasEllipticalCap && !hasStraightCap) {
        
        // Для накидных гаек и шайб с резьбой используем дефолт (58 для квадратных, 40 для круглых), для остальных - значение из параметра
        const nakMeasure = (isNakGaika || isShaibaNarRezba)
          ? nakShaibaPipeMeasure(effectiveFittingLength, bortshaibaLength, shape, isShaibaNarRezba)
          : null;
        const totalConnectionLength = nakMeasure
          ? nakMeasure.visible
          : (Number(effectiveFittingLength) || 100);
        const intermediatePipeLength = nakMeasure
          ? nakMeasure.outside
          : Math.max(0, totalConnectionLength - bortshaibaLength);
        
        // 1. Промежуточная труба меньшего диаметра
        if (intermediatePipeLength > 0 && pipeDiameterForFitting) {
          intermediatePipeId = `cyl-${prefix}-intermediate-${generateUniqueId()}`;
          totalLength += intermediatePipeLength;
          
          const xPos = direction === 'left' ? currentPos - intermediatePipeLength : currentPos;
          
          // Для накидных гаек и шайб с резьбой: фиксированная толщина x3 (как в контурах)
          const { getActualCoating: getActualCoatingConn1 } = require('./pipeThicknessUtils');
          const intermediateThickness1 = (isNakGaika || isShaibaNarRezba) ? 'x3' : (mainPipeDiameter ? determineThickness(mainPipeDiameter, pipeDiameterForFitting, 'Матовый') : 'x2');
          const intermediateCoating1 = (isNakGaika || isShaibaNarRezba) ? getActualCoatingConn1(pipeDiameterForFitting, 'x3', 'Матовый') : 'Матовый';
          
          cylinders.push({
            id: intermediatePipeId,
            x: xPos,
            y: y - pipeDiameterForFitting / 2,
            width: intermediatePipeLength,
            height: pipeDiameterForFitting,
            pipeSpec: { shape: 'round', diameter: pipeDiameterForFitting, thickness: intermediateThickness1, coating: intermediateCoating1 },
            insertionDepth: 0,
          });
          
          balloons.push({
            id: `balloon-${prefix}-intermediate-${generateUniqueId()}`,
            x: xPos + intermediatePipeLength / 2,
            y: y + balloonYOffset,
            text: `Труба Ø${pipeDiameterForFitting}`,
            objects: [intermediatePipeId]
          });
          
          currentPos = direction === 'left' ? currentPos - intermediatePipeLength : currentPos + intermediatePipeLength;
        }
        
        // 2. Фланец/накидная гайка/шайба (используем ту же логику, что и в ЛОГИКЕ 3)
        const rotation = direction === 'left' ? -90 : 90;
        
        if (isShaibaNarRezba) {
          // Для шайб с наружной резьбой: сама шайба и есть фитинг, размещается на конце трубы
          const shaibaLength = fittingInfo.length || BORTSHAIBA_LENGTH;
          const shaibaElementId = `fit-${prefix}-shaiba-${generateUniqueId()}`;
          mainElementId = shaibaElementId;
          totalLength += shaibaLength;
          
          // Логика для "Измененное значение" - шайбы с резьбой: 32мм (круглые) / 54мм (квадратные)
          let dimensionOverride = {};
          if (fittingLength === null || fittingLength === undefined || 
              fittingLength === 40 || fittingLength === '40' || Number(fittingLength) === 40 ||
              fittingLength === 58 || fittingLength === '58' || Number(fittingLength) === 58) {
            dimensionOverride = {
              overrideEnabled: false,
              overrideText: shape === 'square' ? '54' : '32',
            };
          }
          
          fittings.push({
            id: shaibaElementId,
            key: fittingId,
            itemKey: fittingId,
            x: 0, y: 0,
            width: fittingInfo.diameter,
            height: shaibaLength,
            ...(fittingInfo.snapPoints && {
              snapPoints: fittingInfo.snapPoints,
              useOriginalSize: true,
            }),
            correctionOffset: 3,
            groupX: currentPos,
            groupY: y,
            rotation,
            offsetX: fittingInfo.diameter / 2,
            offsetY: shaibaLength,
            ...dimensionOverride,
          });
          
          const xPos = direction === 'left' ? currentPos - shaibaLength / 2 : currentPos + shaibaLength / 2;
          
          balloons.push({
            id: `balloon-${prefix}-shaiba-${generateUniqueId()}`,
            x: xPos,
            y: y + balloonYOffset,
            text: fittingInfo.name || 'Шайба',
            objects: [shaibaElementId]
          });
        } else if (isFreeFlange) {
          // Для свободных фланцев: бортшайба + фланец (от бортшайбы + длина)
          let bortshaibaGroupX = currentPos;
          
          if (shaibaKey && fittingsData[shaibaKey]) {
            const shaibaInfo = fittingsData[shaibaKey];
            bortshaibaId = `fit-${prefix}-bortshaiba-${generateUniqueId()}`;
            
            fittings.push({
              id: bortshaibaId,
              key: shaibaKey,
              itemKey: shaibaKey,
              x: 0, y: 0,
              width: shaibaInfo.diameter,
              height: bortshaibaLength,
              ...(shaibaInfo.snapPoints && {
                snapPoints: shaibaInfo.snapPoints,
                useOriginalSize: true,
              }),
              correctionOffset: 3,
              groupX: currentPos,
              groupY: y,
              rotation,
              offsetX: shaibaInfo.diameter / 2,
              offsetY: bortshaibaLength,
            });
            
            const xPos = direction === 'left' ? currentPos - bortshaibaLength / 2 : currentPos + bortshaibaLength / 2;
            
            balloons.push({
              id: `balloon-${prefix}-bortshaiba-${generateUniqueId()}`,
              x: xPos,
              y: y + balloonYOffset,
              text: shaibaInfo.name || 'Бортшайба',
              objects: [bortshaibaId]
            });
            
            bortshaibaGroupX = currentPos;
          }
          
          // Фланец: от бортшайбы + длина
          const fittingElementId = `fit-${prefix}-conn-${generateUniqueId()}`;
          const fittingActualLength = fittingInfo.length;
          
          mainElementId = fittingElementId;
          // НЕ добавляем к totalLength, так как фланец "надет" на трубу
          
          const flangeGroupX = direction === 'left' 
            ? bortshaibaGroupX + fittingActualLength 
            : bortshaibaGroupX - fittingActualLength;
          
          fittings.push({
            id: fittingElementId,
            key: fittingId,
            itemKey: fittingId,
            x: 0, y: 0,
            width: fittingInfo.diameter,
            height: fittingActualLength,
            groupX: flangeGroupX,
            groupY: y,
            rotation,
            offsetX: fittingInfo.diameter / 2,
            offsetY: fittingActualLength,
            // IGES: торцевые подключения НЕ создают отверстия
            igesCreateHole: false,
          });
          
          const xPos = direction === 'left' ? flangeGroupX - fittingActualLength / 2 : flangeGroupX + fittingActualLength / 2;
          
          balloons.push({
            id: `balloon-${prefix}-conn-${generateUniqueId()}`,
            x: xPos,
            y: y + balloonYOffset,
            text: fittingInfo.name || fittingId,
            objects: [fittingElementId]
          });
        } else if (isNakGaika) {
          // Для накидных гаек: бортшайба + гайка (от бортшайбы + длина)
          let bortshaibaGroupX = currentPos;
          
          if (shaibaKey && fittingsData[shaibaKey]) {
            const shaibaInfo = fittingsData[shaibaKey];
            bortshaibaId = `fit-${prefix}-bortshaiba-${generateUniqueId()}`;
            
            fittings.push({
              id: bortshaibaId,
              key: shaibaKey,
              itemKey: shaibaKey,
              x: 0, y: 0,
              width: shaibaInfo.diameter,
              height: bortshaibaLength,
              ...(shaibaInfo.snapPoints && {
                snapPoints: shaibaInfo.snapPoints,
                useOriginalSize: true,
              }),
              correctionOffset: 3,
              groupX: currentPos,
              groupY: y,
              rotation,
              offsetX: shaibaInfo.diameter / 2,
              offsetY: bortshaibaLength,
            });
            
            const xPos = direction === 'left' ? currentPos - bortshaibaLength / 2 : currentPos + bortshaibaLength / 2;
            
            balloons.push({
              id: `balloon-${prefix}-bortshaiba-${generateUniqueId()}`,
              x: xPos,
              y: y + balloonYOffset,
              text: shaibaInfo.name || 'Бортшайба',
              objects: [bortshaibaId]
            });
            
            bortshaibaGroupX = currentPos;
          }
          
          // Накидная гайка: от бортшайбы + длина
          const fittingElementId = `fit-${prefix}-conn-${generateUniqueId()}`;
          const fittingActualLength = fittingInfo.length;
          
          mainElementId = fittingElementId;
          // НЕ добавляем к totalLength, так как гайка "надета" на трубу
          
          const gaikaGroupX = direction === 'left' 
            ? bortshaibaGroupX + fittingActualLength 
            : bortshaibaGroupX - fittingActualLength;
          
          // Логика для "Измененное значение" - накидные гайки: 32мм (круглые) / 54мм (квадратные)
          let dimensionOverride = {};
          if (fittingLength === null || fittingLength === undefined || 
              fittingLength === 40 || fittingLength === '40' || Number(fittingLength) === 40 ||
              fittingLength === 58 || fittingLength === '58' || Number(fittingLength) === 58) {
            dimensionOverride = {
              overrideEnabled: false,
              overrideText: shape === 'square' ? '54' : '32',
              bortshaibaId: bortshaibaId, // Сохраняем ID бортшайбы для привязки размерной линии
            };
          }
          
          fittings.push({
            id: fittingElementId,
            key: fittingId,
            itemKey: fittingId,
            x: 0, y: 0,
            width: fittingInfo.diameter,
            height: fittingActualLength,
            groupX: gaikaGroupX,
            groupY: y,
            rotation,
            offsetX: fittingInfo.diameter / 2,
            offsetY: fittingActualLength,
            ...dimensionOverride,
          });
          
          const xPos = direction === 'left' ? gaikaGroupX - fittingActualLength / 2 : gaikaGroupX + fittingActualLength / 2;
          
          balloons.push({
            id: `balloon-${prefix}-conn-${generateUniqueId()}`,
            x: xPos,
            y: y + balloonYOffset,
            text: fittingInfo.name || fittingId,
            objects: [fittingElementId]
          });
        }
      }
      // ЛОГИКА 2: С переходом
      else if (hasReducerFromCaps) {
        // Для накидных гаек и шайб с резьбой используем дефолт (58 для квадратных, 40 для круглых), для остальных - значение из параметра
        const nakMeasure = (isNakGaika || isShaibaNarRezba)
          ? nakShaibaPipeMeasure(effectiveFittingLength, bortshaibaLength, shape, isShaibaNarRezba)
          : null;
        const totalConnectionLength = nakMeasure
          ? nakMeasure.visible
          : (Number(effectiveFittingLength) || 100);
        const intermediatePipeLength = nakMeasure
          ? nakMeasure.outside
          : Math.max(0, totalConnectionLength - capLength - bortshaibaLength);
        
        // 1. Промежуточная труба меньшего диаметра
        if (intermediatePipeLength > 0 && pipeDiameterForFitting) {
          intermediatePipeId = `cyl-${prefix}-intermediate-${generateUniqueId()}`;
          totalLength += intermediatePipeLength;
          
          const xPos = direction === 'left' ? currentPos - intermediatePipeLength : currentPos;
          
          // Для накидных гаек и шайб с резьбой: фиксированная толщина x3 (как в контурах)
          const { getActualCoating: getActualCoatingConn2 } = require('./pipeThicknessUtils');
          const intermediateThickness2 = (isNakGaika || isShaibaNarRezba) ? 'x3' : (mainPipeDiameter ? determineThickness(mainPipeDiameter, pipeDiameterForFitting, 'Матовый') : 'x2');
          const intermediateCoating2 = (isNakGaika || isShaibaNarRezba) ? getActualCoatingConn2(pipeDiameterForFitting, 'x3', 'Матовый') : 'Матовый';
          
          cylinders.push({
            id: intermediatePipeId,
            x: xPos,
            y: y - pipeDiameterForFitting / 2,
            width: intermediatePipeLength,
            height: pipeDiameterForFitting,
            pipeSpec: { shape: 'round', diameter: pipeDiameterForFitting, thickness: intermediateThickness2, coating: intermediateCoating2 },
            insertionDepth: 0,
          });
          
          balloons.push({
            id: `balloon-${prefix}-intermediate-${generateUniqueId()}`,
            x: xPos + intermediatePipeLength / 2,
            y: y + balloonYOffset,
            text: `Труба Ø${pipeDiameterForFitting}`,
            objects: [intermediatePipeId]
          });
          
          currentPos = direction === 'left' ? currentPos - intermediatePipeLength : currentPos + intermediatePipeLength;
        }
        
        // 2. Фитинг на конце промежуточной трубы
        if (isShaibaNarRezba) {
          // Для шайб с наружной резьбой: сама шайба и есть фитинг
          const shaibaLength = fittingInfo.length || BORTSHAIBA_LENGTH;
          const shaibaElementId = `fit-${prefix}-shaiba-${generateUniqueId()}`;
          mainElementId = shaibaElementId;
          totalLength += shaibaLength;
          
          const rotation = direction === 'left' ? -90 : 90;
          
          fittings.push({
            id: shaibaElementId,
            key: fittingId,
            itemKey: fittingId,
            x: 0, y: 0,
            width: fittingInfo.diameter,
            height: shaibaLength,
            ...(fittingInfo.snapPoints && {
              snapPoints: fittingInfo.snapPoints,
              useOriginalSize: true,
            }),
            correctionOffset: 3,
            groupX: currentPos,
            groupY: y,
            rotation,
            offsetX: fittingInfo.diameter / 2,
            offsetY: shaibaLength,
          });
          
          const xPos = direction === 'left' ? currentPos - shaibaLength / 2 : currentPos + shaibaLength / 2;
          
          balloons.push({
            id: `balloon-${prefix}-shaiba-${generateUniqueId()}`,
            x: xPos,
            y: y + balloonYOffset,
            text: fittingInfo.name || 'Шайба',
            objects: [shaibaElementId]
          });
          
          currentPos = direction === 'left' ? currentPos - shaibaLength : currentPos + shaibaLength;
        } else if (isWeldedFlange) {
          // Для приварных фланцев с переходом: фланец "надевается" на промежуточную трубу
          const fittingElementId = `fit-${prefix}-conn-${generateUniqueId()}`;
          const fittingActualLength = fittingInfo.length;
          
          mainElementId = fittingElementId;
          // НЕ добавляем к totalLength, так как фланец "надет" на трубу
          
          const rotation = direction === 'left' ? -90 : 90;
          
          // Позиция фланца: от конца трубы + длина фланца
          const flangeGroupX = direction === 'left' 
            ? currentPos + fittingActualLength 
            : currentPos - fittingActualLength;
          
          fittings.push({
            id: fittingElementId,
            key: fittingId,
            itemKey: fittingId,
            x: 0, y: 0,
            width: fittingInfo.diameter,
            height: fittingActualLength,
            groupX: flangeGroupX,
            groupY: y,
            rotation,
            offsetX: fittingInfo.diameter / 2,
            offsetY: fittingActualLength,
            // IGES: торцевые подключения НЕ создают отверстия
            igesCreateHole: false,
          });
        
          const xPos = direction === 'left' ? flangeGroupX - fittingActualLength / 2 : flangeGroupX + fittingActualLength / 2;
        
          balloons.push({
            id: `balloon-${prefix}-conn-${generateUniqueId()}`,
            x: xPos,
            y: y + balloonYOffset,
            text: fittingInfo.name || fittingId,
            objects: [fittingElementId]
          });
        } else if (needsBortshaiba && shaibaKey && fittingsData[shaibaKey]) {
          // Для накидных гаек и свободных фланцев: бортшайба + фитинг
          const shaibaInfo = fittingsData[shaibaKey];
          bortshaibaId = `fit-${prefix}-bortshaiba-${generateUniqueId()}`;
          totalLength += bortshaibaLength;
          
          const rotation = direction === 'left' ? -90 : 90;
          
          fittings.push({
            id: bortshaibaId,
            key: shaibaKey,
            itemKey: shaibaKey,
            x: 0, y: 0,
            width: shaibaInfo.diameter,
            height: bortshaibaLength,
            ...(shaibaInfo.snapPoints && {
              snapPoints: shaibaInfo.snapPoints,
              useOriginalSize: true,
            }),
            correctionOffset: 3,
            groupX: currentPos,
            groupY: y,
            rotation,
            offsetX: shaibaInfo.diameter / 2,
            offsetY: bortshaibaLength,
          });
          
          const bortshaibaXPos = direction === 'left' ? currentPos - bortshaibaLength / 2 : currentPos + bortshaibaLength / 2;
          
          balloons.push({
            id: `balloon-${prefix}-bortshaiba-${generateUniqueId()}`,
            x: bortshaibaXPos,
            y: y + balloonYOffset,
            text: shaibaInfo.name || 'Бортшайба',
            objects: [bortshaibaId]
          });
          
          // Добавляем фланец/накидную гайку (надет на трубу, от бортшайбы + длина)
          const fittingElementId = `fit-${prefix}-conn-${generateUniqueId()}`;
          const fittingActualLength = fittingInfo.length;
          
          mainElementId = fittingElementId;
          // НЕ добавляем к totalLength, так как фланец/гайка "надет" на трубу
          
          // Позиция фланца/гайки: от бортшайбы + длина фитинга
          const fittingGroupX = direction === 'left' 
            ? currentPos + fittingActualLength 
            : currentPos - fittingActualLength;
          
          fittings.push({
            id: fittingElementId,
            key: fittingId,
            itemKey: fittingId,
            x: 0, y: 0,
            width: fittingInfo.diameter,
            height: fittingActualLength,
            groupX: fittingGroupX,
            groupY: y,
            rotation,
            offsetX: fittingInfo.diameter / 2,
            offsetY: fittingActualLength,
          });
          
          const fittingXPos = direction === 'left' ? fittingGroupX - fittingActualLength / 2 : fittingGroupX + fittingActualLength / 2;
          
          balloons.push({
            id: `balloon-${prefix}-conn-${generateUniqueId()}`,
            x: fittingXPos,
            y: y + balloonYOffset,
            text: fittingInfo.name || fittingId,
            objects: [fittingElementId]
          });
        }
      }
      // ЛОГИКА 3: С заглушкой (эллиптической или прямой)
      else if (hasEllipticalCap || hasStraightCap) {
        // Для накидных гаек и шайб с резьбой используем дефолт (58 для квадратных, 40 для круглых), для остальных - значение из параметра
        const nakMeasure = (isNakGaika || isShaibaNarRezba)
          ? nakShaibaPipeMeasure(effectiveFittingLength, bortshaibaLength, shape, isShaibaNarRezba)
          : null;
        const totalConnectionLength = nakMeasure
          ? nakMeasure.visible
          : (Number(effectiveFittingLength) || 100);
        let intermediatePipeLength;
        let capStartPos = currentPos; // Позиция начала промежуточной трубы (после заглушки)
        
        if (hasStraightCap) {
          // Прямая заглушка входит в размер гребёнки
          // Промежуточная труба начинается от края заглушки
          capStartPos = direction === 'left' ? currentPos - capLength : currentPos + capLength;
          // Для прямой заглушки: вылет отсчитывается ОТ заглушки, НЕ вычитаем длину заглушки
          intermediatePipeLength = nakMeasure
            ? nakMeasure.outside
            : Math.max(0, totalConnectionLength - bortshaibaLength);
        } else {
          // Эллиптическая заглушка выступает наружу
          // Для накидных гаек и шайб с резьбой: вылет отсчитывается от заглушки, НЕ вычитаем длину заглушки
          if (isNakGaika || isShaibaNarRezba) {
            intermediatePipeLength = nakMeasure.outside;
          } else {
          intermediatePipeLength = Math.max(0, totalConnectionLength - capLength - bortshaibaLength);
          }
        }
        
        // 1. Промежуточная труба меньшего диаметра
        if (intermediatePipeLength > 0 && pipeDiameterForFitting) {
          intermediatePipeId = `cyl-${prefix}-intermediate-${generateUniqueId()}`;
          totalLength += intermediatePipeLength;
          
          const xPos = direction === 'left' ? capStartPos - intermediatePipeLength : capStartPos;
          
          // Для накидных гаек и шайб с резьбой: фиксированная толщина x3 (как в контурах)
          const { getActualCoating: getActualCoatingConn3 } = require('./pipeThicknessUtils');
          const intermediateThickness3 = (isNakGaika || isShaibaNarRezba) ? 'x3' : (mainPipeDiameter ? determineThickness(mainPipeDiameter, pipeDiameterForFitting, 'Матовый') : 'x2');
          const intermediateCoating3 = (isNakGaika || isShaibaNarRezba) ? getActualCoatingConn3(pipeDiameterForFitting, 'x3', 'Матовый') : 'Матовый';
          
          cylinders.push({
            id: intermediatePipeId,
            x: xPos,
            y: y - pipeDiameterForFitting / 2,
            width: intermediatePipeLength,
            height: pipeDiameterForFitting,
            pipeSpec: { shape: 'round', diameter: pipeDiameterForFitting, thickness: intermediateThickness3, coating: intermediateCoating3 },
            insertionDepth: 0,
          });
          
          balloons.push({
            id: `balloon-${prefix}-intermediate-${generateUniqueId()}`,
            x: xPos + intermediatePipeLength / 2,
            y: y + balloonYOffset,
            text: `Труба Ø${pipeDiameterForFitting}`,
            objects: [intermediatePipeId]
          });
          
          currentPos = direction === 'left' ? capStartPos - intermediatePipeLength : capStartPos + intermediatePipeLength;
        } else {
          // Если промежуточной трубы нет, currentPos = capStartPos
          currentPos = capStartPos;
        }
        
        // 2. Фланец/накидная гайка (надет на промежуточную трубу)
        // Для свободных фланцев: фланец ПЕРЕД бортшайбой (ближе к гребенке)
        // Для накидных гаек: порядок - труба → бортшайба → гайка
        // Для шайб с наружной резьбой: порядок - труба → шайба (сама шайба = фитинг)
        if (isShaibaNarRezba) {
          // Для шайб с наружной резьбой: сама шайба и есть фитинг
          const shaibaLength = fittingInfo.length || BORTSHAIBA_LENGTH;
          const shaibaElementId = `fit-${prefix}-shaiba-${generateUniqueId()}`;
          mainElementId = shaibaElementId;
          totalLength += shaibaLength;
          
          const rotation = direction === 'left' ? -90 : 90;
          
          // Логика для "Измененное значение" - шайбы с резьбой: 32мм (круглые) / 54мм (квадратные)
          let dimensionOverride = {};
          if (fittingLength === null || fittingLength === undefined || 
              fittingLength === 40 || fittingLength === '40' || Number(fittingLength) === 40 ||
              fittingLength === 58 || fittingLength === '58' || Number(fittingLength) === 58) {
            dimensionOverride = {
              overrideEnabled: false,
              overrideText: shape === 'square' ? '54' : '32',
            };
          }
          
          fittings.push({
            id: shaibaElementId,
            key: fittingId,
            itemKey: fittingId,
            x: 0, y: 0,
            width: fittingInfo.diameter,
            height: shaibaLength,
            ...(fittingInfo.snapPoints && {
              snapPoints: fittingInfo.snapPoints,
              useOriginalSize: true,
            }),
            correctionOffset: 3,
            groupX: currentPos,
            groupY: y,
            rotation,
            offsetX: fittingInfo.diameter / 2,
            offsetY: shaibaLength,
            ...dimensionOverride,
          });
          
          const xPos = direction === 'left' ? currentPos - shaibaLength / 2 : currentPos + shaibaLength / 2;
          
          balloons.push({
            id: `balloon-${prefix}-shaiba-${generateUniqueId()}`,
            x: xPos,
            y: y + balloonYOffset,
            text: fittingInfo.name || 'Шайба',
            objects: [shaibaElementId]
          });
          
          currentPos = direction === 'left' ? currentPos - shaibaLength : currentPos + shaibaLength;
        } else if (isFreeFlange) {
          // Для свободных фланцев: сначала бортшайба (на конце трубы), потом фланец (надет поверх)
          // Бортшайба визуально на конце, фланец "надет" на трубу (между бортшайбой и гребенкой)
          
          const rotation = direction === 'left' ? -90 : 90;
          let bortshaibaGroupX = currentPos;
          
          // 1. Добавляем бортшайбу на конец промежуточной трубы
          if (shaibaKey && fittingsData[shaibaKey]) {
            const shaibaInfo = fittingsData[shaibaKey];
            bortshaibaId = `fit-${prefix}-bortshaiba-${generateUniqueId()}`;
            
            fittings.push({
              id: bortshaibaId,
              key: shaibaKey,
              itemKey: shaibaKey,
              x: 0, y: 0,
              width: shaibaInfo.diameter,
              height: bortshaibaLength,
              ...(shaibaInfo.snapPoints && {
                snapPoints: shaibaInfo.snapPoints,
                useOriginalSize: true,
              }),
              correctionOffset: 3,
              groupX: currentPos,
              groupY: y,
              rotation,
              offsetX: shaibaInfo.diameter / 2,
              offsetY: bortshaibaLength,
            });
            
            const bortshaibaXPos = direction === 'left' ? currentPos - bortshaibaLength / 2 : currentPos + bortshaibaLength / 2;
            
            balloons.push({
              id: `balloon-${prefix}-bortshaiba-${generateUniqueId()}`,
              x: bortshaibaXPos,
              y: y + balloonYOffset,
              text: shaibaInfo.name || 'Бортшайба',
              objects: [bortshaibaId]
            });
            
            bortshaibaGroupX = currentPos;
          }
          
          // 2. Фланец "надет" на трубу, его groupX = groupX бортшайбы + длина фланца
          // (как в этажном коллекторе: flangeX = leftBortshaibaTop + fLength)
          const fittingElementId = `fit-${prefix}-conn-${generateUniqueId()}`;
          const fittingActualLength = fittingInfo.length;
          
          mainElementId = fittingElementId;
          totalLength += fittingActualLength;
          
          // Позиция фланца: от бортшайбы + длина фланца
          const flangeGroupX = direction === 'left' 
            ? bortshaibaGroupX + fittingActualLength 
            : bortshaibaGroupX - fittingActualLength;
          
          fittings.push({
            id: fittingElementId,
            key: fittingId,
            itemKey: fittingId,
            x: 0, y: 0,
            width: fittingInfo.diameter,
            height: fittingActualLength,
            groupX: flangeGroupX,
            groupY: y,
            rotation,
            offsetX: fittingInfo.diameter / 2,
            offsetY: fittingActualLength,
            // IGES: торцевые подключения НЕ создают отверстия
            igesCreateHole: false,
          });
          
          const xPos = direction === 'left' ? flangeGroupX - fittingActualLength / 2 : flangeGroupX + fittingActualLength / 2;
          
          balloons.push({
            id: `balloon-${prefix}-conn-${generateUniqueId()}`,
            x: xPos,
            y: y + balloonYOffset,
            text: fittingInfo.name || fittingId,
            objects: [fittingElementId]
          });
        } else if (isNakGaika) {
          // Для накидных гаек: сначала бортшайба (на конце трубы), потом гайка (от бортшайбы + длина)
          // Логика такая же, как для свободных фланцев
          
          const rotation = direction === 'left' ? -90 : 90;
          let bortshaibaGroupX = currentPos;
          
          // 1. Добавляем бортшайбу на конец промежуточной трубы
          if (shaibaKey && fittingsData[shaibaKey]) {
            const shaibaInfo = fittingsData[shaibaKey];
            bortshaibaId = `fit-${prefix}-bortshaiba-${generateUniqueId()}`;
            
            fittings.push({
              id: bortshaibaId,
              key: shaibaKey,
              itemKey: shaibaKey,
              x: 0, y: 0,
              width: shaibaInfo.diameter,
              height: bortshaibaLength,
              ...(shaibaInfo.snapPoints && {
                snapPoints: shaibaInfo.snapPoints,
                useOriginalSize: true,
              }),
              correctionOffset: 3,
              groupX: currentPos,
              groupY: y,
              rotation,
              offsetX: shaibaInfo.diameter / 2,
              offsetY: bortshaibaLength,
            });
            
            const xPos = direction === 'left' ? currentPos - bortshaibaLength / 2 : currentPos + bortshaibaLength / 2;
            
            balloons.push({
              id: `balloon-${prefix}-bortshaiba-${generateUniqueId()}`,
              x: xPos,
              y: y + balloonYOffset,
              text: shaibaInfo.name || 'Бортшайба',
              objects: [bortshaibaId]
            });
            
            bortshaibaGroupX = currentPos;
          }
          
          // 2. Накидная гайка: от бортшайбы + длина гайки (как в этажном коллекторе)
          const fittingElementId = `fit-${prefix}-conn-${generateUniqueId()}`;
          const fittingActualLength = fittingInfo.length;
          
          mainElementId = fittingElementId;
          totalLength += fittingActualLength;
          
          // Позиция гайки: от бортшайбы + длина гайки
          const gaikaGroupX = direction === 'left' 
            ? bortshaibaGroupX + fittingActualLength 
            : bortshaibaGroupX - fittingActualLength;
          
          // Логика для "Измененное значение" - накидные гайки: 32мм (круглые) / 54мм (квадратные)
          let dimensionOverride = {};
          const shouldApplyOverride = fittingLength === null || fittingLength === undefined || 
                                      fittingLength === 40 || fittingLength === '40' || Number(fittingLength) === 40 ||
                                      fittingLength === 58 || fittingLength === '58' || Number(fittingLength) === 58;
          
          if (shouldApplyOverride) {
            dimensionOverride = {
              overrideEnabled: false,
              overrideText: shape === 'square' ? '54' : '32',
              bortshaibaId: bortshaibaId, // Сохраняем ID бортшайбы для привязки размерной линии
            };
          }
          
          fittings.push({
            id: fittingElementId,
            key: fittingId,
            itemKey: fittingId,
            x: 0, y: 0,
            width: fittingInfo.diameter,
            height: fittingActualLength,
            groupX: gaikaGroupX,
            groupY: y,
            rotation,
            offsetX: fittingInfo.diameter / 2,
            offsetY: fittingActualLength,
            ...dimensionOverride,
          });
          
          const xPos = direction === 'left' ? gaikaGroupX - fittingActualLength / 2 : gaikaGroupX + fittingActualLength / 2;
          
          balloons.push({
            id: `balloon-${prefix}-conn-${generateUniqueId()}`,
            x: xPos,
            y: y + balloonYOffset,
            text: fittingInfo.name || fittingId,
            objects: [fittingElementId]
          });
        } else if (isWeldedFlange) {
          // Для приварных фланцев: фланец "надевается" на промежуточную трубу
          // (как в этажном: flangeX = connectionPointX + fLength)
          const fittingElementId = `fit-${prefix}-conn-${generateUniqueId()}`;
          const fittingActualLength = fittingInfo.length;
          
          mainElementId = fittingElementId;
          // НЕ добавляем к totalLength, так как фланец "надет" на трубу
          
          const rotation = direction === 'left' ? -90 : 90;
          
          // Позиция фланца: от конца трубы + длина фланца
          const flangeGroupX = direction === 'left' 
            ? currentPos + fittingActualLength 
            : currentPos - fittingActualLength;
          
          fittings.push({
            id: fittingElementId,
            key: fittingId,
            itemKey: fittingId,
            x: 0, y: 0,
            width: fittingInfo.diameter,
            height: fittingActualLength,
            groupX: flangeGroupX,
            groupY: y,
            rotation,
            offsetX: fittingInfo.diameter / 2,
            offsetY: fittingActualLength,
            // IGES: торцевые подключения НЕ создают отверстия
            igesCreateHole: false,
          });
        
          const xPos = direction === 'left' ? flangeGroupX - fittingActualLength / 2 : flangeGroupX + fittingActualLength / 2;
        
          balloons.push({
            id: `balloon-${prefix}-conn-${generateUniqueId()}`,
            x: xPos,
            y: y + balloonYOffset,
            text: fittingInfo.name || fittingId,
            objects: [fittingElementId]
          });
        } else {
          // Для других фитингов: просто на currentPos
          const fittingElementId = `fit-${prefix}-conn-${generateUniqueId()}`;
          const fittingActualLength = endFittingAutoLength(fittingId, fittingLength, fittingInfo.length);
          
          mainElementId = fittingElementId;
          totalLength += fittingActualLength;
          
          const rotation = direction === 'left' ? -90 : 90;
          
          let dimensionOverride = {};
          
          fittings.push({
            id: fittingElementId,
            key: fittingId,
            itemKey: fittingId,
            x: 0, y: 0,
            width: fittingInfo.diameter,
            height: fittingActualLength,
            groupX: currentPos,
            groupY: y,
            rotation,
            offsetX: fittingInfo.diameter / 2,
            offsetY: fittingActualLength,
            ...dimensionOverride,
          });
        
          const xPos = direction === 'left' ? currentPos - fittingActualLength / 2 : currentPos + fittingActualLength / 2;
        
          balloons.push({
            id: `balloon-${prefix}-conn-${generateUniqueId()}`,
            x: xPos,
            y: y + balloonYOffset,
            text: fittingInfo.name || fittingId,
            objects: [fittingElementId]
          });
        }
      }
      
    } else {
      // === ПРОСТАЯ ЛОГИКА: обычный фитинг без промежуточной трубы ===
      const fittingElementId = `fit-${prefix}-conn-${generateUniqueId()}`;
      const catalogLength = fittingInfo.length;
      const length = endFittingAutoLength(fittingId, fittingLength, catalogLength);
      let dimensionOverride = {};
      
      mainElementId = fittingElementId;
      
      const rotation = direction === 'left' ? -90 : 90;
      
      // Если есть прямая заглушка, фитинг должен начинаться от заглушки, а не от гребенки
      let fittingStartPos = currentPos;
      if (hasStraightCap) {
        fittingStartPos = direction === 'left' ? currentPos - capLength : currentPos + capLength;
      }
      
      // Для фланцев (любых): они "надеваются" на гребенку/заглушку
      // flangeOffset = isFlange ? fLength : 0 (как в этажном коллекторе)
      const flangeOffset = isFlange ? length : 0;
      const fittingGroupX = direction === 'left' 
        ? fittingStartPos + flangeOffset 
        : fittingStartPos - flangeOffset;
      
      // Фланец не добавляет длину к totalLength (он "надет" на трубу)
      // Обычные фитинги добавляют длину
      if (!isFlange) {
        totalLength += length;
      }
      
      fittings.push({
        id: fittingElementId,
        key: fittingId,
        itemKey: fittingId,
        x: 0, y: 0,
        width: fittingInfo.diameter,
        height: length,
        groupX: fittingGroupX,
        groupY: y,
        rotation,
        offsetX: fittingInfo.diameter / 2,
        offsetY: length,
        ...dimensionOverride,
      });
      
      welds.push({ 
        id: `weld-${prefix}-conn-${generateUniqueId()}`, 
        x: startX, 
        y, 
        objects: [mainPipeId, fittingElementId] 
      });
      
      const xPos = direction === 'left' ? fittingGroupX - length / 2 : fittingGroupX + length / 2;
      
      balloons.push({
        id: `balloon-${prefix}-conn-${generateUniqueId()}`,
        x: xPos,
        y: y + balloonYOffset,
        text: fittingInfo.name || fittingId,
        objects: [fittingElementId]
      });
    }
  }
  
  return { 
    cylinders, 
    fittings, 
    welds, 
    balloons, 
    totalLength, 
    mainElementId, 
    intermediatePipeId, 
    bortshaibaId 
  };
};

